import {
  ReactNode,
  useEffect,
  useState,
  useContext,
  createContext,
  useMemo,
  useCallback,
} from 'react';
import { useAuth } from './AuthProvider';

const API_BASE_URL = import.meta.env.VITE_API_SERVER + '/api' || '/api';

export interface ProcessTypeConfig {
  displayName: string;
  color: string;
  type: string;
}

export interface ProcessSettings {
  id: string;
  processTypes: Record<string, ProcessTypeConfig>;
  updatedAt: number;
}

export interface ProcessSettingsContextModel {
  settings: ProcessSettings | null;
  isLoading: boolean;
  updateProcessType: (
    type: string,
    config: Partial<ProcessTypeConfig>
  ) => Promise<void>;
  getProcessTypeConfig: (type: string) => ProcessTypeConfig;
  deleteProcessType: (type: string) => Promise<void>;
  refreshSettings: () => Promise<void>;
}

const defaultProcessTypes: Record<string, ProcessTypeConfig> = {
  'Move In': {
    displayName: 'Move In',
    color: '#10B981',
    type: 'Move In',
  },
  'Make Ready': {
    displayName: 'Make Ready',
    color: '#F59E0B',
    type: 'Make Ready',
  },
  Notice: {
    displayName: 'Notice',
    color: '#3B82F6',
    type: 'Notice',
  },
  Inspection: {
    displayName: 'Inspection',
    color: '#F43F5E',
    type: 'Inspection',
  },
  Turnover: {
    displayName: 'Turnover',
    color: '#8B5CF6',
    type: 'Turnover',
  },
};

export const ProcessSettingsContext = createContext<ProcessSettingsContextModel>(
  {} as ProcessSettingsContextModel
);

export function useProcessSettings(): ProcessSettingsContextModel {
  return useContext(ProcessSettingsContext);
}

export interface ProcessSettingsProviderProps {
  children: ReactNode | null;
}

export function ProcessSettingsProvider({
  children,
}: ProcessSettingsProviderProps) {
  const { userData, user } = useAuth();
  const [settings, setSettings] = useState<ProcessSettings | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);

  // Use 'default' as organization ID since settings are shared across the project
  // In the future, this could be derived from a team or organization field
  const organizationId = 'default';

  // Helper to get Firebase Auth token
  const getAuthToken = useCallback(async (): Promise<string | null> => {
    if (!user) return null;
    try {
      return await user.getIdToken();
    } catch (error) {
      console.error('Error getting auth token:', error);
      return null;
    }
  }, [user]);

  const fetchSettings = useCallback(async () => {
    if (!organizationId) {
      setIsLoading(false);
      return;
    }

    // Don't try to fetch if user is not logged in yet
    if (!user) {
      setIsLoading(false);
      // Set default settings while waiting for auth
      setSettings({
        id: organizationId,
        processTypes: defaultProcessTypes,
        updatedAt: Date.now(),
      });
      return;
    }

    setIsLoading(true);
    try {
      const token = await getAuthToken();
      if (!token) {
        throw new Error('No authentication token available');
      }

      const url = `${API_BASE_URL}/process-settings?organizationId=${organizationId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`Failed to fetch process settings: ${response.status}`);
      }

      const data: ProcessSettings = await response.json();
      setSettings(data);
    } catch (error) {
      console.error('Error fetching process settings:', error);
      // Set default settings on error
      setSettings({
        id: organizationId,
        processTypes: defaultProcessTypes,
        updatedAt: Date.now(),
      });
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, getAuthToken, user]);

  // Load settings on mount and when organizationId changes
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateProcessType = useCallback(
    async (type: string, config: Partial<ProcessTypeConfig>) => {
      if (!organizationId || !settings) return;

      const existingConfig = settings.processTypes[type] || {
        type,
        displayName: type,
        color: '#6B7280',
      };

      const updatedSettings: ProcessSettings = {
        ...settings,
        processTypes: {
          ...settings.processTypes,
          [type]: {
            ...existingConfig,
            ...config,
          },
        },
        updatedAt: Date.now(),
      };

      try {
        const token = await getAuthToken();
        if (!token) {
          throw new Error('No authentication token available');
        }

        const url = `${API_BASE_URL}/process-settings`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedSettings),
        });

        if (!response.ok) {
          throw new Error('Failed to update process settings');
        }

        const result = await response.json();
        setSettings(result.settings);
      } catch (error) {
        console.error('Error updating process type:', error);
        throw error;
      }
    },
    [organizationId, settings, getAuthToken]
  );

  const deleteProcessType = useCallback(
    async (type: string) => {
      if (!organizationId || !settings) return;

      const { [type]: _, ...remainingTypes } = settings.processTypes;

      const updatedSettings: ProcessSettings = {
        ...settings,
        processTypes: remainingTypes,
        updatedAt: Date.now(),
      };

      try {
        const token = await getAuthToken();
        if (!token) {
          throw new Error('No authentication token available');
        }

        const url = `${API_BASE_URL}/process-settings`;
        const response = await fetch(url, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
          body: JSON.stringify(updatedSettings),
        });

        if (!response.ok) {
          throw new Error('Failed to delete process type');
        }

        const result = await response.json();
        setSettings(result.settings);
      } catch (error) {
        console.error('Error deleting process type:', error);
        throw error;
      }
    },
    [organizationId, settings, getAuthToken]
  );

  const getProcessTypeConfig = useCallback(
    (type: string): ProcessTypeConfig => {
      if (!settings || !settings.processTypes[type]) {
        return {
          type,
          displayName: type,
          color: '#6B7280',
        };
      }
      return settings.processTypes[type];
    },
    [settings]
  );

  const values = useMemo(
    () => ({
      settings,
      isLoading,
      updateProcessType,
      getProcessTypeConfig,
      deleteProcessType,
      refreshSettings: fetchSettings,
    }),
    [
      settings,
      isLoading,
      updateProcessType,
      getProcessTypeConfig,
      deleteProcessType,
      fetchSettings,
    ]
  );

  return (
    <ProcessSettingsContext.Provider value={values}>
      {children}
    </ProcessSettingsContext.Provider>
  );
}
